import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from "@angular/core";
import { DatePipe, DecimalPipe } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { Router } from "@angular/router";
import { SharedModule } from "../../../../shared/shared.module";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { SelectComponent } from "../../../../shared/modules/select/select.component";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { WidgetDataApiService } from "../../services/widget-data-api.service";
import { WidgetDataFacadeService } from "../../services/widget-data-facade.service";
import { Widget } from "../../models/dashboard.model";
import { COUVERTURE_COMMANDES_DATA } from "../../models/data.model";
import {
  CouvertureCommandeRow,
  CouvertureStatut,
  deriveCouverture,
  mapRawToRow,
} from "./couverture-commandes.model";
import {
  CouvertureCommandesDialogComponent,
  CouvertureCommandesDialogData,
} from "./couverture-commandes-dialog.component";

interface CouvRowVM {
  row: CouvertureCommandeRow;
  statut: CouvertureStatut;
  manque: number;
  reservableNow: number;
}

const STATUT_ORDER: CouvertureStatut[] = ["MANQUE", "A_RESERVER", "COUVERT"];
const LS_HORIZON = "justup.widget.couvertureCommandes.horizon";

@Component({
  selector: "vl-widget-couverture-commandes",
  standalone: true,
  imports: [
    SharedModule,
    DatePipe,
    DecimalPipe,
    ChipsComponent,
    SelectComponent,
    WidgetTitleComponent,
    MatIcon,
    MatTooltip,
  ],
  host: { class: "widget-component" },
  templateUrl: "./widget-couverture-commandes.component.html",
  styleUrl: "./widget-couverture-commandes.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetCouvertureCommandesComponent {
  widget = input<Widget>();

  private router = inject(Router);
  private dialogs = inject(DialogsService);
  private api = inject(WidgetDataApiService);
  private widgetDataFacade = inject(WidgetDataFacadeService);

  /** Self-fetching : données chargées via l'endpoint paramétrable. */
  data = signal<COUVERTURE_COMMANDES_DATA | null>(null);
  loading = signal<boolean>(true);

  /** Horizon (jours) — réglable, persisté en localStorage. */
  horizon = signal<number>(this._readHorizon());
  horizonOptions = [
    { name: "Aujourd'hui", value: 0 },
    { name: "Cette semaine", value: 7 },
    { name: "14 jours", value: 14 },
    { name: "Ce mois", value: 30 },
    { name: "60 jours", value: 60 },
    { name: "Toutes", value: 3650 },
  ];

  /** Statut affiché (onglet). null tant que l'auto-sélection n'a pas eu lieu. */
  statut = signal<CouvertureStatut | null>(null);
  private userPickedTab = signal<boolean>(false);

  /** Lignes normalisées (hors prestations — pas de stock à couvrir). */
  rows = computed<CouvRowVM[]>(() => {
    const raw = this.data()?.produits ?? [];
    return raw
      .filter((p) => !p.isPrestation)
      .map((p) => {
        const row = mapRawToRow(p);
        const d = deriveCouverture(row);
        return {
          row,
          statut: d.statut,
          manque: d.manque,
          reservableNow: d.reservableNow,
        };
      });
  });

  nbManque = computed(() => this.rows().filter((r) => r.statut === "MANQUE").length);
  nbAReserver = computed(
    () => this.rows().filter((r) => r.statut === "A_RESERVER").length,
  );
  nbCouvert = computed(
    () => this.rows().filter((r) => r.statut === "COUVERT").length,
  );

  /** Couverture globale = Σ(min(besoin, réservé+éligible)) / Σ besoin. */
  couvrablePct = computed(() => {
    const rows = this.rows();
    let besoin = 0;
    let couvre = 0;
    for (const { row } of rows) {
      besoin += row.besoinUG;
      couvre += Math.min(
        row.besoinUG,
        row.engageUG + Math.max(0, row.stockEligibleUG),
      );
    }
    return besoin > 0 ? Math.round((couvre / besoin) * 100) : 100;
  });

  /** Top 10 du statut courant, tri urgence (1ʳᵉ expé ASC). */
  filteredRows = computed<CouvRowVM[]>(() => {
    const st = this.statut();
    if (!st) return [];
    return this.rows()
      .filter((r) => r.statut === st)
      .sort((a, b) => {
        const da = a.row.premierDeparture ?? "9999-12-31";
        const db = b.row.premierDeparture ?? "9999-12-31";
        return da.localeCompare(db);
      })
      .slice(0, 10);
  });

  constructor() {
    // (Re)fetch au mount + à chaque changement d'horizon.
    effect(
      () => {
        const h = this.horizon();
        untracked(() => this.fetch(h));
      },
      { allowSignalWrites: true },
    );

    // Auto-sélection : premier onglet non vide (Manque → À réserver → Couvert).
    effect(
      () => {
        const data = this.data();
        if (!data || this.userPickedTab()) return;
        untracked(() => {
          const counts: Record<CouvertureStatut, number> = {
            MANQUE: this.nbManque(),
            A_RESERVER: this.nbAReserver(),
            COUVERT: this.nbCouvert(),
          };
          const first = STATUT_ORDER.find((s) => counts[s] > 0);
          this.statut.set(first ?? "MANQUE");
        });
      },
      { allowSignalWrites: true },
    );

    // Self-fetching : rejouer le fetch au "Actualiser" du dashboard (refreshTick).
    let firstTick = true;
    effect(
      () => {
        this.widgetDataFacade.refreshTick();
        if (firstTick) {
          firstTick = false;
          return;
        }
        untracked(() => this.fetch(this.horizon()));
      },
      { allowSignalWrites: true },
    );
  }

  private fetch(horizonJours: number): void {
    this.loading.set(true);
    this.api.loadCouvertureCommandes(horizonJours).subscribe({
      next: (d) => {
        this.data.set(d);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  setHorizon(value: number): void {
    this.horizon.set(value);
    try {
      localStorage.setItem(LS_HORIZON, String(value));
    } catch {
      /* localStorage indispo — non bloquant */
    }
  }

  private _readHorizon(): number {
    try {
      const v = parseInt(localStorage.getItem(LS_HORIZON) ?? "", 10);
      return Number.isFinite(v) ? v : 14;
    } catch {
      return 14;
    }
  }

  selectTab(st: CouvertureStatut): void {
    this.userPickedTab.set(true);
    this.statut.set(st);
  }

  onSelect(vm: CouvRowVM): void {
    this.router.navigateByUrl(`/preparation/produit/${vm.row.articleId}`);
  }

  openDialog(): void {
    const d = this.data();
    if (!d) return;
    this.dialogs.open(
      CouvertureCommandesDialogComponent,
      "min(95vw, 1200px)",
      "min(90vh, 800px)",
      { data: d } satisfies CouvertureCommandesDialogData,
    );
  }
}
